<template>
  <b-container class="details mb-2">
    <b-row class="mb-2 tabHeader">
      <b-col cols="12" class="font-weight-bold" v-t="'productDetails.tabHeader.performanceScenarios'"></b-col>
    </b-row>
    <template v-if="periods.period1.scenariosMin != null">
      <template v-if="isMop">
        <b-row>
          <b-col cols="12" class="text-center m-2" v-t="'common.best'"></b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <!-- One row per scenario, performance+percentage stacked inside each value cell instead
            of two separate physical rows. Gated on compactScenarios (default false); without it the
            two-row markup below is used. -->
            <b-table v-if="compactScenarios" striped hover borderless :items="compactBestResults" :fields="bestShownFields" class="results w-100 compact-scenarios-table">
              <template v-slot:cell()="row">
                <template v-if="row.field.key === 'label'">
                  <div>{{ row.value }}</div>
                  <div v-if="row.item.caption" class="small text-muted font-italic">{{ row.item.caption }}</div>
                </template>
                <template v-else-if="periods[row.field.key]">
                  <div class="text-right">{{ formatCurrency(row.item[row.field.key + 'Performance']) }}</div>
                  <div class="text-right small text-muted">{{ formatPercentage(row.item[row.field.key + 'Percentage']) }}</div>
                </template>
                <template v-else>{{ row.value }}</template>
              </template>
            </b-table>
            <b-table v-else hover :items="bestResults" :fields="bestShownFields" :tbody-tr-class="stripeRowClass" class="results w-100">
              <template v-slot:cell()="row">
                <template v-if="periods[row.field.key]">
                  <span class="text-right">
                    {{ formattedValue(row.item.field, periods[row.field.key].scenariosMax[row.item.field]) }}
                  </span>
                </template>
                <template v-else>
                  {{ row.value }}
                  <div v-if="period1Caption(row)" class="small text-muted">{{ period1Caption(row) }}</div>
                </template>
              </template>
            </b-table>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" class="text-center m-2" v-t="'common.worst'"></b-col>
        </b-row>
      </template>
      <b-row>
        <b-col cols="12">
          <b-table v-if="compactScenarios" striped hover borderless :items="compactWorstResults" :fields="worstShownFields" class="results w-100 compact-scenarios-table">
            <template v-slot:cell()="row">
              <template v-if="row.field.key === 'label'">
                <div>{{ row.value }}</div>
                <div v-if="row.item.caption" class="small text-muted font-italic">{{ row.item.caption }}</div>
              </template>
              <template v-else-if="periods[row.field.key]">
                <div class="text-right">{{ formatCurrency(row.item[row.field.key + 'Performance']) }}</div>
                <div class="text-right small text-muted">{{ formatPercentage(row.item[row.field.key + 'Percentage']) }}</div>
              </template>
              <template v-else>{{ row.value }}</template>
            </template>
          </b-table>
          <b-table v-else hover :items="worstResults" :fields="worstShownFields" :tbody-tr-class="stripeRowClass" class="results w-100">
            <template v-slot:cell()="row">
              <template v-if="periods[row.field.key]">
                <span class="text-right">
                  {{ formattedValue(row.item.field, periods[row.field.key].scenariosMin[row.item.field]) }}
                </span>
              </template>
              <template v-else>
                {{ row.value }}
                <div v-if="period1Caption(row)" class="small text-muted">{{ period1Caption(row) }}</div>
              </template>
            </template>
          </b-table>
        </b-col>
      </b-row>
      <b-row v-if="productType !== 4">
        <b-col cols="12">
          <b-table striped hover :items="aieResults" :fields="aieShownFields" class="results w-100">
            <template v-slot:cell()="row">
              <template v-if="periods[row.field.key]">
                <span class="text-right">
                  {{
                    formattedValue(
                      row.item.field,
                      periods[row.field.key]['scenarios' + row.item.case][row.item.field + 'Aie']
                    )
                  }}
                </span>
              </template>
              <template v-else>{{ row.value }}</template>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </template>
    <b-row v-else>
      <b-col cols="12" class="text-center font-weight-bold"> N/A </b-col>
    </b-row>
  </b-container>
</template>
<script>
export default {
  name: 'ProductScenarios',
  props: {
    value: {},
    isMop: { type: Boolean, default: false },
    productType: { type: Number },
    rtsVersion: { type: Number },
    // `{ [scenarioKey]: caption|null }` — an extra "Termina dopo N anni/mesi" line under the
    // scenario's own NAME (the label column's first/"performance" sub-row, user-confirmed position —
    // not under the Period1 value cell), for an autocallable/callable IC product (the new
    // config-driven detail page's own PerformanceScenariosSection.vue builds this map; period2/period3
    // are never captioned, only period1 ever has one). Default empty object.
    period1Captions: { type: Object, default: () => ({}) },
    // Switches the "best"/"worst" tables to the one-row-per-scenario layout — see the template's
    // own comment on the b-table pairs above. Default false.
    compactScenarios: { type: Boolean, default: false },
  },
  computed: {
    periods: function () {
      return this.value
    },
    bestShownFields: function () {
      return [
        {
          key: 'label',
          label: this.$t('common.scenario'),
          class: 'text-left',
        },
        ...this.periodsHeaders(),
      ]
    },
    worstShownFields: function () {
      return [
        {
          key: 'label',
          label: this.$t('common.scenario'),
        },
        ...this.periodsHeaders(),
      ]
    },
    aieShownFields: function () {
      return [
        {
          key: 'label',
          label: this.$t('common.insuredEvent'),
        },
        ...this.periodsHeaders(),
      ]
    },
    bestResults: function () {
      var elements = ['Stress', 'Unfavourable', 'Moderate', 'Favourable']
      if (this.rtsVersion === 2) {
        elements = ['Minimum', 'Stress', 'Unfavourable', 'Moderate', 'Favourable']
      }
      return this.extractFrom('Max', elements)
    },
    worstResults: function () {
      var elements = ['Stress', 'Unfavourable', 'Moderate', 'Favourable']
      if (this.rtsVersion === 2) {
        elements = ['Minimum', 'Stress', 'Unfavourable', 'Moderate', 'Favourable']
      }
      return this.extractFrom('Min', elements)
    },
    // (compactScenarios only): one item per scenario, both performance and percentage values
    // for every period embedded directly (periodNPerformance/periodNPercentage) instead of extractFrom's
    // two-separate-flat-items shape — the compact table's own cell template reads these straight off
    // row.item, no formattedValue(field-name-sniffing) dispatch needed. Mirrors bestResults/worstResults'
    // own Max/Min split (user-extended to IBIPs/MOP, not IC-only anymore) via compactResultsFor's own
    // scenariosKey param.
    compactBestResults: function () {
      return this.compactResultsFor('Max')
    },
    compactWorstResults: function () {
      return this.compactResultsFor('Min')
    },
    aieResults: function () {
      let temp = [
        {
          case: 'Min',
          field: 'performance',
          label: this.isMop ? this.$t('search.resultTable.aieMin') : this.$t('common.standard'),
        },
        {
          case: 'Min',
          field: 'percentage',
          label: '',
        },
      ]
      if (this.isMop) {
        temp.push(
          {
            case: 'Max',
            field: 'performance',
            label: this.$t('search.resultTable.aieMax'),
          },
          {
            case: 'Max',
            field: 'percentage',
            label: '',
          }
        )
      }
      return temp
    },
  },
  methods: {
    periodsHeaders() {
      const period1 = {
        key: this.periods.period1.scenariosYear != null ? 'period1' : ' ',
        label: this.periods.period1.scenariosYear ? this.periods.period1.scenariosYear.toString() : '',
        class: 'text-right',
      }
      const period2 = {
        key: this.periods.period2.scenariosYear != null ? 'period2' : ' ',
        label:
          this.periods.period2.scenariosYear != null
            ? this.periods.period2.scenariosYear !== 0
              ? this.periods.period2.scenariosYear.toString()
              : 'RHP/2'
            : '',
        class: 'text-right',
      }
      const period3 = {
        key: this.periods.period3.scenariosYear != null ? 'period3' : ' ',
        label:
          this.periods.period3.scenariosYear != null
            ? this.periods.period3.scenariosYear !== 0
              ? this.periods.period3.scenariosYear.toString()
              : 'RHP'
            : '',
        class: 'text-right',
      }
      const emptyPeriod = {
        label: '',
      }

      const ret = [period1]
      if (this.periods.period2.scenariosYear !== null) {
        ret.push(period2)
      } else {
        ret.unshift(emptyPeriod)
      }
      if (this.periods.period3.scenariosYear !== null) {
        ret.push(period3)
      } else {
        ret.unshift(emptyPeriod)
      }
      return ret
    },
    extractFrom(scenarios, elements) {
      return elements.flatMap((key, elementIndex) => {
        // The performance/percentage row PAIR for one scenario shares one
        // stripe color, alternating per scenario — not bootstrap's own `striped` prop, which alternates
        // every single physical <tr> and would split each pair across two different colors. See
        // stripeRowClass()/tbody-tr-class below, which reads this back off the rendered item.
        const stripeGroup = elementIndex % 2

        var performance = {
          field: 'performance' + key,
          stripeGroup,
          label: this.$t('productDetails.' + key.toLowerCase()),
          period1: this.periods.period1['scenarios' + scenarios]
            ? this.periods.period1['scenarios' + scenarios][key]
            : '',
          period2: this.periods.period2['scenarios' + scenarios]
            ? this.periods.period2['scenarios' + scenarios][key]
            : '',
          period3: this.periods.period3['scenarios' + scenarios]
            ? this.periods.period3['scenarios' + scenarios][key]
            : '',
        }

        var percentage = {
          field: 'percentage' + key,
          stripeGroup,
          label: '',
          period1: this.periods.period1['scenarios' + scenarios]
            ? this.periods.period1['scenarios' + scenarios][key]
            : '',
          period2: this.periods.period2['scenarios' + scenarios]
            ? this.periods.period2['scenarios' + scenarios][key]
            : '',
          period3: this.periods.period3['scenarios' + scenarios]
            ? this.periods.period3['scenarios' + scenarios][key]
            : '',
        }

        return [performance, percentage]
      })
    },
    stripeRowClass(item) {
      return item && item.stripeGroup === 1 ? 'scenario-stripe' : null
    },
    // (compactScenarios): one item per scenario, both performance and percentage values for
    // every period embedded directly (periodNPerformance/periodNPercentage) — the compact table's own
    // cell template reads these straight off row.item, no formattedValue(field-name-sniffing) dispatch
    // needed. `scenariosKey` ('Min'/'Max') mirrors bestResults/worstResults' own split — for SOP
    // products (compactWorstResults only, `isMop` false) scenariosMin/scenariosMax carry the same
    // duplicated value per adaptPeriod's own SOP branch, so this stays correct there too.
    compactResultsFor(scenariosKey) {
      let elements = ['Stress', 'Unfavourable', 'Moderate', 'Favourable']
      if (this.rtsVersion === 2) {
        elements = ['Minimum', 'Stress', 'Unfavourable', 'Moderate', 'Favourable']
      }
      return elements.map((key) => ({
        field: key,
        label: this.$t('productDetails.' + key.toLowerCase()),
        caption: this.period1Captions[key] || null,
        period1Performance: this.compactPeriodValue(1, 'performance', key, scenariosKey),
        period1Percentage: this.compactPeriodValue(1, 'percentage', key, scenariosKey),
        period2Performance: this.compactPeriodValue(2, 'performance', key, scenariosKey),
        period2Percentage: this.compactPeriodValue(2, 'percentage', key, scenariosKey),
        period3Performance: this.compactPeriodValue(3, 'performance', key, scenariosKey),
        period3Percentage: this.compactPeriodValue(3, 'percentage', key, scenariosKey),
      }))
    },
    compactPeriodValue(periodNumber, kind, scenarioKey, scenariosKey) {
      const bucket = this.periods['period' + periodNumber]['scenarios' + scenariosKey]
      return bucket ? bucket[kind + scenarioKey] : null
    },
    formatPercentage(val) {
      return val != undefined && val != null && !isNaN(val) ? this.$n(val, 'percent') : '-'
    },
    formatCurrency(val) {
      return val != undefined && val != null && !isNaN(val)
        ? this.$n(val, { key: 'currency', currency: this.periods.currency })
        : '-'
    },
    formattedValue(key, value) {
      return key.includes('performance') ? this.formatCurrency(value) : this.formatPercentage(value)
    },
    // Only the label column carries a caption, and only on the bottom ("percentage") sub-row of each
    // scenario pair, alongside the percentage VALUES it describes ("Termina dopo
    // N anni/mesi" sits on the same row as the return-percentage figures, not the absolute/monetary
    // row above it) — the performance sub-row above it shares the same scenario/period, showing the
    // caption twice would be redundant. `row.item.field` is the same regardless of which column is
    // being rendered (b-table's generic `cell()` slot re-runs per {row, column} pair against the same
    // row.item), so this only fires for the label column thanks to the row.field.key guard — the
    // caption is conceptually about Period1, it just renders in a different column/row now.
    period1Caption(row) {
      if (row.field.key !== 'label' || !row.item.field.startsWith('percentage')) {
        return null
      }
      const scenarioKey = row.item.field.replace('percentage', '')
      return this.period1Captions[scenarioKey] || null
    },
  },
}
</script>

<!-- Custom striping — the performance/percentage row PAIR for one scenario
shares one color, alternating per scenario, instead of bootstrap's own `striped` prop,
which alternates every single physical <tr> and would split each pair across two different colors.
`/deep/` is required because these rows are generated by `<b-table>`'s own internal render, not
authored directly in this component's template — same reasoning DetailCard.vue's own `/deep/ .row`
rules document. Matches bootstrap's own default stripe color ($table-accent-bg,
node_modules/bootstrap/scss/_variables.scss) so this doesn't look different from any other `.results`
table on the page that still uses `striped`. -->
<style lang="scss" scoped>
/deep/ .scenario-stripe {
  background-color: rgba(0, 0, 0, 0.05);
}

// bootstrap-vue's own `borderless` prop (template) only strips the `.table-borderless` cell
// border-top rule — a residual line would still show between rows.
// Forced off explicitly here; `!important` + `/deep/` for the same reason as `.scenario-stripe` above — these cells are
// generated by `<b-table>`'s own internal render, not authored directly in this template.
/deep/ .compact-scenarios-table td,
/deep/ .compact-scenarios-table th {
  border: none !important;
}
</style>
